/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Button,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import {
    convertDateFilterStringToDate,
    formatDate,
    truncateString,
} from "../../../../utils/helpers";
import { useDebounce } from "../../../../hooks/helpers";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import useToast from "../../../../hooks/useToast";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import InfoIcon from "../../../../icons/Info";
import {
    ICollectionsNote,
    ICollectionsNoteDocument,
} from "../../types/clients.types";
import { getCollectionsNoteUserName } from "../../helpers/clients.helpers";
import { useFetchCollectionsNotes } from "../../hooks/clients.queries";
import { useDeleteCollectionsNote } from "../../hooks/clients.mutations";
import AddCollectionsNote from "./AddCollectionsNote/AddCollectionsNote";
import EditCollectionsNote from "./EditCollectionsNote/EditCollectionsNote";
import CollectionsNoteActivity from "./CollectionsNoteActivity/CollectionsNoteActivity";
import CollectionsNoteDetails from "./CollectionsNoteDetails/CollectionsNoteDetails";

export default function ClientCollectionsNotes() {
    const [documentsInView, setDocumentsInView] = React.useState<
        ICollectionsNoteDocument[] | null
    >(null);
    const [currentDocumentIndex, setCurrentDocumentIndex] = React.useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useParams();
    const clientId = params.clientId as string;

    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const authorNameFilter = searchParams.get("author_name");
    const modalInView = searchParams.get("modal");
    const selectedNoteId = searchParams.get("collections_note_id");

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const hasActiveFilters = Boolean(
        fromDateFilter || toDateFilter || authorNameFilter
    );

    const authorName = useDebounce(authorNameFilter || "", 500);

    const canWrite = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_COLLECTIONS_NOTES_WRITE,
    ]);
    const isAdmin = useDoesUserHaveRole([
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
    ]);

    const loggedInUser = useFetchUserDetails();

    const { data, isLoading, isSuccess, error } = useFetchCollectionsNotes(
        clientId,
        {
            author_name: authorName,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            page: pageFilter,
            limit: limitFilter,
        }
    );

    const selectedNoteFromList = data?.data.find(
        (note) => note.collections_note_id === selectedNoteId
    );

    const { data: selectedNoteResponse } = useFetchCollectionsNotes(
        clientId,
        { collections_note_id: selectedNoteId || "", page: 1, limit: 1 },
        Boolean(selectedNoteId) && isSuccess && !selectedNoteFromList
    );

    const selectedNote =
        selectedNoteFromList || selectedNoteResponse?.data?.[0];

    const addIsMutating = useIsMutating(["add-collections-note", clientId]);
    const addDocumentsIsMutating = useIsMutating([
        "add-collections-note-documents",
    ]);
    const editIsMutating = useIsMutating(["edit-collections-note"]);

    const deleteCollectionsNote = useDeleteCollectionsNote();

    const queryClient = useQueryClient();
    const { toast } = useToast();

    const canModifyNote = (note: ICollectionsNote) =>
        canWrite &&
        (isAdmin || loggedInUser.data?.user_id === note.author?.user_id);

    const handleSearchInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("author_name");
        else searchParams.set("author_name", e.target.value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const toggleModalInView = (modal: string) => {
        if (modal === modalInView) searchParams.delete("modal");
        else searchParams.set("modal", modal);
        setSearchParams(searchParams);
    };

    const handleViewDocuments = (documents: ICollectionsNoteDocument[]) => {
        if (documents.length === 0) return;
        setDocumentsInView(documents);
        setCurrentDocumentIndex(0);
    };

    const handleCloseDocumentViewer = () => {
        setDocumentsInView(null);
        setCurrentDocumentIndex(0);
    };

    const handleOpenNoteView = (noteId: string) => {
        searchParams.set("collections_note_id", noteId);
        searchParams.delete("modal");
        setSearchParams(searchParams);
    };

    const handleOpenNoteModal = (noteId: string, modal: string) => {
        searchParams.set("collections_note_id", noteId);
        searchParams.set("modal", modal);
        setSearchParams(searchParams);
    };

    const handleCloseNote = () => {
        searchParams.delete("collections_note_id");
        searchParams.delete("modal");
        searchParams.delete("activity_page");
        searchParams.delete("activity_limit");
        setSearchParams(searchParams, { replace: true });
    };

    const handleCloseActivity = () => {
        searchParams.delete("activity_page");
        searchParams.delete("activity_limit");
        toggleModalInView("collections_notes_activity");
    };

    const handleDeleteCollectionsNote = () => {
        if (!selectedNoteId) return;

        deleteCollectionsNote.mutate(selectedNoteId, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({
                    queryKey: [clientId, "collections-notes"],
                });
                toast({
                    mode: "success",
                    message:
                        res.message || "Collections note deleted successfully",
                });
                handleCloseNote();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Cannot delete collections note at this moment, please try again",
                });
            },
        });
    };

    const columns: ColumnDef<ICollectionsNote>[] = [
        {
            accessorKey: "date_created",
            header: "Date created",
            cell: ({ row }) => formatDate(row.original.date_created),
        },
        {
            accessorKey: "author",
            header: "Author",
            cell: ({ row }) => (
                <span className="capitalize">
                    {getCollectionsNoteUserName(row.original.author)}
                </span>
            ),
        },
        {
            accessorKey: "description",
            header: "Description",
            cell: ({ row }) => (
                <div className="items-center flex gap-x-1">
                    <span>{truncateString(row.original.description, 80)}</span>
                    {row.original.updated_by ? (
                        <ActionButton
                            size="auto"
                            variant="normal"
                            actionTitle={`Updated on: ${formatDate(
                                row.original.date_updated
                            )}, \n Updated by: ${getCollectionsNoteUserName(
                                row.original.updated_by
                            )}`}
                        >
                            <InfoIcon />
                        </ActionButton>
                    ) : null}
                </div>
            ),
        },
        {
            accessorKey: "documents",
            header: "Attachments",
            cell: ({ row }) =>
                row.original.documents.length > 0 ? (
                    <Button
                        variant="normal"
                        size="auto"
                        className="p-0 underline font-normal text-textLink"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewDocuments(row.original.documents);
                        }}
                    >
                        {`${row.original.documents.length} attachment${
                            row.original.documents.length === 1 ? "" : "s"
                        }`}
                    </Button>
                ) : (
                    <span className="text-textSec">No attachments</span>
                ),
        },
        {
            accessorKey: "collections_note_id",
            header: "",
            cell: ({ row }) =>
                canModifyNote(row.original) ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton onClick={(e) => e.stopPropagation()} />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenNoteModal(
                                        row.original.collections_note_id,
                                        "edit_collections_note"
                                    );
                                }}
                            >
                                Edit
                            </DropdownItem>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenNoteModal(
                                        row.original.collections_note_id,
                                        "delete_collections_note"
                                    );
                                }}
                            >
                                Delete
                            </DropdownItem>
                        </DropdownContent>
                    </Dropdown>
                ) : null,
        },
    ];

    return (
        <>
            <Dialog open={modalInView === "add_collections_note"}>
                <DialogContent
                    title="Add collections note"
                    variant="center"
                    handleCloseDialog={() =>
                        toggleModalInView("add_collections_note")
                    }
                    saveText="Add note"
                    cancelText="Cancel"
                    isSubmitBtnDisabled={
                        addIsMutating > 0 || addDocumentsIsMutating > 0
                    }
                    isCancelBtnDisabled={
                        addIsMutating > 0 || addDocumentsIsMutating > 0
                    }
                    submitBtnFormValue="add-collections-note"
                >
                    <AddCollectionsNote
                        onFormSubmit={() =>
                            toggleModalInView("add_collections_note")
                        }
                    />
                </DialogContent>
            </Dialog>
            {modalInView === "edit_collections_note" && selectedNote ? (
                <Dialog open>
                    <DialogContent
                        title="Edit collections note"
                        variant="center"
                        handleCloseDialog={handleCloseNote}
                        saveText="Save changes"
                        cancelText="Cancel"
                        isSubmitBtnDisabled={
                            editIsMutating > 0 || addDocumentsIsMutating > 0
                        }
                        isCancelBtnDisabled={
                            editIsMutating > 0 || addDocumentsIsMutating > 0
                        }
                        submitBtnFormValue="edit-collections-note"
                    >
                        <EditCollectionsNote
                            collectionsNote={selectedNote}
                            onFormSubmit={handleCloseNote}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <AlertDialog open={modalInView === "delete_collections_note"}>
                <AlertDialogContent
                    title="Are you sure you want to delete this collections note?"
                    description="This will also delete its attachments. This action cannot be reversed."
                    isDeleting
                    onCancelClick={handleCloseNote}
                    saveText="Delete note"
                    cancelText="Cancel"
                    isSaveBtnDisabled={deleteCollectionsNote.isLoading}
                    isCancelBtnDisabled={deleteCollectionsNote.isLoading}
                    onSaveClick={handleDeleteCollectionsNote}
                />
            </AlertDialog>
            <Dialog open={modalInView === "collections_notes_activity"}>
                <DialogContent
                    title="Collections notes activity"
                    variant="center"
                    handleCloseDialog={handleCloseActivity}
                    showFooter={false}
                >
                    <CollectionsNoteActivity clientId={clientId} />
                </DialogContent>
            </Dialog>
            {selectedNote && !modalInView ? (
                <Dialog open>
                    <DialogContent
                        title="Collections note"
                        handleCloseDialog={handleCloseNote}
                        showFooter={false}
                    >
                        <CollectionsNoteDetails
                            collectionsNote={selectedNote}
                            canModify={canModifyNote(selectedNote)}
                            onEdit={() =>
                                handleOpenNoteModal(
                                    selectedNote.collections_note_id,
                                    "edit_collections_note"
                                )
                            }
                            onDelete={() =>
                                handleOpenNoteModal(
                                    selectedNote.collections_note_id,
                                    "delete_collections_note"
                                )
                            }
                            onViewDocument={(document) =>
                                handleViewDocuments([document])
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            {documentsInView ? (
                <Dialog open>
                    <DialogContent
                        title={
                            documentsInView[currentDocumentIndex]
                                ?.document_name || "Attachment"
                        }
                        handleCloseDialog={handleCloseDocumentViewer}
                        showFooter={false}
                    >
                        <>
                            {documentsInView.length > 1 ? (
                                <div className="flex items-center justify-between mb-[--spacing-items]">
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() =>
                                            setCurrentDocumentIndex((prev) =>
                                                prev > 0
                                                    ? prev - 1
                                                    : documentsInView.length - 1
                                            )
                                        }
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-caption font-medium">
                                        {`Attachment ${
                                            currentDocumentIndex + 1
                                        } of ${documentsInView.length}`}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() =>
                                            setCurrentDocumentIndex((prev) =>
                                                prev <
                                                documentsInView.length - 1
                                                    ? prev + 1
                                                    : 0
                                            )
                                        }
                                    >
                                        Next
                                    </Button>
                                </div>
                            ) : null}
                            <ViewFile
                                url={
                                    documentsInView[currentDocumentIndex]
                                        ?.url || ""
                                }
                                contentType={
                                    documentsInView[currentDocumentIndex]
                                        ?.metadata?.content_type || ""
                                }
                                title={
                                    documentsInView[currentDocumentIndex]
                                        ?.document_name || "Attachment"
                                }
                            />
                        </>
                    </DialogContent>
                </Dialog>
            ) : null}

            <Card classNames="mb-[--spacing-header]">
                <div className="flex flex-col gap-[--spacing-items]">
                    <SearchInput
                        containerClass="w-[400px] max-w-full"
                        placeholder="Author"
                        onChange={handleSearchInputChange}
                        defaultValue={authorNameFilter || ""}
                    />
                    <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                        <Popover>
                            <PopoverTrigger asChild>
                                <FilterButton>Date</FilterButton>
                            </PopoverTrigger>
                            <PopoverContent align="start">
                                <DatePicker
                                    label="From"
                                    placeholderText="MM/DD/YYYY"
                                    selected={fromDateFilterToDate}
                                    onChange={handleFromDateFilterChange}
                                    maxDate={
                                        toDateFilterToDate ||
                                        new Date(Date.now())
                                    }
                                    isClearable
                                />
                                <DatePicker
                                    label="To"
                                    placeholderText="MM/DD/YYYY"
                                    selected={toDateFilterToDate}
                                    onChange={handleToDateFilterChange}
                                    minDate={fromDateFilterToDate}
                                    isClearable
                                />
                            </PopoverContent>
                        </Popover>
                        <div className="flex items-center flex-wrap gap-[--spacing-items]">
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    toggleModalInView(
                                        "collections_notes_activity"
                                    )
                                }
                            >
                                View activity
                            </Button>
                            {canWrite ? (
                                <Button
                                    aria-label="Add collections note"
                                    onClick={() =>
                                        toggleModalInView(
                                            "add_collections_note"
                                        )
                                    }
                                >
                                    Add collections note
                                </Button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Card>

            {isLoading ? (
                <Loader dataTestId="collections-notes-loader" />
            ) : null}
            {error ? (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ||
                        "Cannot display collections notes at this time please try again later"
                    }
                />
            ) : null}

            {data && isSuccess && data.data.length === 0 ? (
                <ListState
                    isError={false}
                    stateHelperText="Collections notes will appear here when added"
                    emptyMessage="No collections note yet"
                    emptyBtnProps={{
                        showButton: canWrite && !hasActiveFilters,
                        buttonText: "Add collections note",
                        onButtonClick: () =>
                            toggleModalInView("add_collections_note"),
                    }}
                />
            ) : null}

            {data && isSuccess && data.data.length > 0 ? (
                <Table
                    data={data.data}
                    columns={columns}
                    handleRowClick={(row) =>
                        handleOpenNoteView(row.original.collections_note_id)
                    }
                    hasPagination={data.total_count > limitFilter}
                    pagination={
                        <Pagination
                            totalCount={data.total_count}
                            totalPages={data.total_pages}
                            currentPage={pageFilter}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                            limit={limitFilter}
                        />
                    }
                />
            ) : null}
        </>
    );
}
